import React, { useRef } from 'react';

import { ValidationErrors } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { Form } from 'react-final-form';

import Condition from 'components/Condition';
import { mergeFormValues, persistDecorator, resetPersist } from 'new/form/persist';
import { NewProgressBar } from 'new/form/progress';
import {
  useInquiryProcessConfig,
  useResetInquiryProgress,
} from 'new/form/state/inquiryProcessConfig/hooks';
import { useShowProgressBar } from 'utils/hooks/useShowProgressBar';

import { revalidateFields } from './mutators';

type Props<FieldTypes> = {
  children: React.ReactNode;
  initialValues: FieldTypes | null;
  handleSubmit: (values: FieldTypes) => Promise<boolean>;
  // Setting the values to FieldTypes leads to a strange type error for the persistDecorarator. Therefore, we use any here.
  validate?: (values: any) => ValidationErrors | Promise<ValidationErrors>;
};

const useHandleSubmit = <FieldTypes,>(handleSubmit: Props<FieldTypes>['handleSubmit']) => {
  const resetInquiryProgress = useResetInquiryProgress();
  return async (values: FieldTypes) => {
    const submitSuccessful = await handleSubmit(values);
    if (submitSuccessful) {
      resetInquiryProgress();
      resetPersist();
    }
  };
};

export function InquiryProcessForm<FieldTypes extends Partial<Record<string, any>> | undefined>({
  children,
  initialValues,
  handleSubmit,
  validate,
}: Props<FieldTypes>) {
  const {
    formStateData: { initialFormValues, formName },
  } = useInquiryProcessConfig();
  const showProgressBar = useShowProgressBar();

  const submitHandler = useHandleSubmit<FieldTypes>(handleSubmit);

  // Merge session storage values with initial form values to ensure the complete
  // structure is maintained. This is important because sensitive fields (like password)
  // are stripped from session storage, but the form still needs the full structure.
  //
  // We track whether we've received "real" initial values (e.g., inquiry details in edit mode).
  // This allows the form to update when async data loads, but prevents re-initialization
  // when navigating between pages (session storage preserves user's in-progress work).
  const hasReceivedInitialValuesRef = useRef(false);
  const initialValuesRef = useRef<FieldTypes | null>(null);

  // Update the ref if:
  // 1. We haven't set it yet, OR
  // 2. We haven't received real initial values yet AND initialValues prop has changed
  //    (this handles async loading of inquiry details in edit mode)
  const hasInitialValuesFromProps = initialValues !== null && initialValues !== initialFormValues;
  if (
    initialValuesRef.current === null ||
    (!hasReceivedInitialValuesRef.current && hasInitialValuesFromProps)
  ) {
    initialValuesRef.current = mergeFormValues(initialFormValues as FieldTypes, initialValues);
    if (hasInitialValuesFromProps) {
      hasReceivedInitialValuesRef.current = true;
    }
  }

  return (
    <Form
      decorators={[persistDecorator]}
      validate={validate}
      subscription={{ submitting: true, pristine: true }}
      initialValues={initialValuesRef.current}
      mutators={{ ...arrayMutators, revalidateFields }}
      onSubmit={submitHandler as (values: Record<string, any>) => Promise<void>}
      render={({ handleSubmit, submitting, pristine }) => (
        <form onSubmit={handleSubmit} id={formName}>
          <Condition condition={showProgressBar}>
            <NewProgressBar />
          </Condition>
          {children}
        </form>
      )}
    />
  );
}
